import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

function StatusBadge({ status }) {
  const map = {
    'Em elaboração': 'bg-violet-100 text-violet-700',
    'Enviado': 'bg-yellow-100 text-yellow-700',
    'Negociação': 'bg-orange-100 text-orange-700',
    'Fechado': 'bg-green-100 text-green-700',
    'Perdido': 'bg-red-100 text-red-700'
  };
  return <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium ${map[status] || 'bg-gray-100 text-gray-700'}`}>{status}</span>;
}

export default function ProjectCard({ p, view = 'grid' }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!isMenuOpen) return undefined;

    const closeMenu = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };
    const closeMenuOnEscape = (event) => {
      if (event.key === 'Escape') setIsMenuOpen(false);
    };

    document.addEventListener('mousedown', closeMenu);
    document.addEventListener('keydown', closeMenuOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeMenu);
      document.removeEventListener('keydown', closeMenuOnEscape);
    };
  }, [isMenuOpen]);

  const formatCurrency = (value) => {
    if (value == null) return '';
    let num = null;
    if (typeof value === 'number') num = value;
    else {
      // remove currency symbols and spaces, normalize thousand/separator
      const cleaned = String(value).replace(/[^0-9,.-]/g, '');
      // if contains comma as decimal separator, replace comma with dot
      const normalized = cleaned.indexOf(',') > -1 && cleaned.indexOf('.') === -1
        ? cleaned.replace(',', '.')
        : cleaned.replace(/\./g, '').replace(',', '.');
      num = parseFloat(normalized);
    }
    if (Number.isNaN(num)) return String(value);
    return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const formatDate = (date) => {
    if (!date) return null;

    // helper to format a Date instance as dd/mm/yyyy
    const fmt = (dt) => {
      const dd = String(dt.getDate()).padStart(2, '0');
      const mm = String(dt.getMonth() + 1).padStart(2, '0');
      const yyyy = dt.getFullYear();
      return `${dd}/${mm}/${yyyy}`;
    };

    // Date object
    if (date instanceof Date) {
      if (isNaN(date.getTime())) return null;
      return fmt(date);
    }

    // Number (timestamp in seconds or milliseconds)
    if (typeof date === 'number') {
      const ts = String(date).length === 10 ? date * 1000 : date;
      const dt = new Date(ts);
      if (isNaN(dt.getTime())) return null;
      return fmt(dt);
    }

    // String handling: try several common formats
    if (typeof date === 'string') {
      const s = date.trim();

      // brazilian format: 08/05/2024 or 08/05/2024 às 10:30
      const br = s.match(/^(\d{2})\/(\d{2})\/(\d{4})(?:\s+às\s+\d{1,2}:\d{2})?$/i);
      if (br) {
        const day = parseInt(br[1], 10);
        const month = parseInt(br[2], 10) - 1;
        const year = parseInt(br[3], 10);
        const dt = new Date(year, month, day);
        if (!isNaN(dt.getTime())) return fmt(dt);
      }

      // numeric string timestamp
      if (/^\d+$/.test(s)) {
        let n = parseInt(s, 10);
        if (s.length === 10) n = n * 1000;
        const dt = new Date(n);
        if (!isNaN(dt.getTime())) return fmt(dt);
      }

      // ISO or other parseable format
      const parsed = new Date(s);
      if (!isNaN(parsed.getTime())) return fmt(parsed);

      return null;
    }

    return null;
  };

  const displayValue = formatCurrency(p.value);
  const createdAt = p.createdAt || p.created_at || p.created || p.date || p.updated || p.updatedAt || p.updated_at || null;
  const displayDate = formatDate(createdAt);

  return (
    <>
      {/* <Link to={`/system/proposals/${p.id}`} className="block"> */}
      <div className="block">
        <div className={`group bg-white rounded-2xl p-5 border hover:shadow-md transition relative ${isMenuOpen ? 'z-30' : 'z-0'} ${view==='list' ? 'flex items-center justify-between' : ''}`}>
        <div>
          <div className="flex items-center justify-between gap-4">
            {/* Status badge hidden temporarily
            <StatusBadge status={p.status} />
            */}
            <div className="text-sm text-gray-800">{p.id}</div>
          </div>

          <h3 className="mt-3 font-semibold text-gray-800 text-lg text-left">{p.title}</h3>
          <div className="text-sm text-gray-800">Cliente: {p.client}</div>

          {displayDate && <div className="text-sm text-gray-800">Data de criação: {displayDate}</div>}

          <div className="mt-4 text-sm text-gray-800">{displayValue}</div>
          {/* Atualizado há X dias - exibido anteriormente. Temporariamente oculto. */}
          {/* <div className="mt-2 text-sm text-gray-400">Atualizado há {p.updated}</div> */}
        </div>

        <div ref={menuRef} className="absolute top-3 right-3 z-30">
          <button
            type="button"
            aria-label={`Ações do orçamento ${p.id}`}
            aria-expanded={isMenuOpen}
            aria-haspopup="menu"
            onClick={() => setIsMenuOpen(open => !open)}
            className="!m-0 !rounded-lg !bg-blue-600 !p-2 !text-white transition-colors hover:!bg-purple-600 active:!bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-300"
          >
            <span aria-hidden="true" className="text-xl leading-none">...</span>
          </button>

          {isMenuOpen && (
            <div role="menu" className="absolute right-0 z-40 mt-2 w-56 overflow-hidden rounded-xl border border-gray-300 bg-gray-100 shadow-lg divide-y divide-gray-300">
              {/* <Link
                to={`/system/proposals/${p.id}`}
                role="menuitem"
                onClick={() => setIsMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-2.5 text-sm font-normal text-gray-800 transition-colors hover:bg-purple-100 hover:text-purple-900 focus:bg-purple-100 focus:text-purple-900 focus:outline-none active:bg-purple-200"
              >
                <span aria-hidden="true" className="w-4 text-center">o</span> Visualizar
              </Link> */}
              {/* <button type="button" role="menuitem" onClick={() => setIsMenuOpen(false)} className="!m-0 !flex !w-full items-center gap-3 !rounded-none !bg-transparent px-4 py-2.5 text-left text-sm font-normal !text-gray-800 transition-colors hover:!bg-purple-100 hover:!text-purple-900 focus:!bg-purple-100 focus:!text-purple-900 focus:outline-none active:!bg-purple-200">
                <span aria-hidden="true" className="w-4 text-center">/</span> Editar
              </button> */}
              <Link to={`/system/contracts/new/${p.id}`} role="menuitem" onClick={() => setIsMenuOpen(false)} className="flex items-center gap-3 px-4 py-2.5 text-sm font-normal text-gray-800 transition-colors hover:bg-purple-100 hover:text-purple-900 focus:bg-purple-100 focus:text-purple-900 focus:outline-none active:bg-purple-200">
                <span aria-hidden="true" className="w-4 text-center">[=]</span> Gerar contrato
              </Link>
              {/* <button type="button" role="menuitem" onClick={() => setIsMenuOpen(false)} className="!m-0 !flex !w-full items-center gap-3 !rounded-none !bg-transparent px-4 py-2.5 text-left text-sm font-normal !text-gray-800 transition-colors hover:!bg-purple-100 hover:!text-purple-900 focus:!bg-purple-100 focus:!text-purple-900 focus:outline-none active:!bg-purple-200">
                <span aria-hidden="true" className="w-4 text-center">+</span> Marcar como negócio fechado
              </button> */}
              {/* <button type="button" role="menuitem" onClick={() => setIsMenuOpen(false)} className="!m-0 !flex !w-full items-center gap-3 !rounded-none !bg-transparent px-4 py-2.5 text-left text-sm font-normal !text-gray-800 transition-colors hover:!bg-purple-100 hover:!text-purple-900 focus:!bg-purple-100 focus:!text-purple-900 focus:outline-none active:!bg-purple-200">
                <span aria-hidden="true" className="w-4 text-center">x</span> Excluir
              </button> */}
            </div>
          )}
        </div>
        </div>
      </div>
      {/* </Link> */}
    </>
  );
}
